import type { YueduWordRows } from "./yuedu-word-rows.js";

/**
 * The popover a word in a passage opens: its reading, a few words of English,
 * a link to its 词典 entry and a Save.
 *
 * One `<wa-popover>` serves every word on the page. Its anchor is set here
 * rather than through `for`, which would need an id on every word and has the
 * component toggle itself on the anchor's clicks as well as this script. Web
 * Awesome closes it on Escape, handing focus back to the word, and on a press
 * anywhere outside it.
 *
 * **A press opens it, and so does a mouse resting on a word.** A press is a
 * click, a tap or Enter on the word, which is a link to its entry, so a reader
 * whose script never ran still reaches the entry. A press with a modifier
 * still follows the link. A mouse resting on a word for {@link hoverDelay}
 * opens it as well, and leaving both the word and the popover closes one that
 * hovering opened. One that a press opened stays until it is dismissed.
 *
 * The contents are copies of the word's row (`yuedu-word-rows.ts`), the save
 * control among them, bound by whoever is handed {@link YueduWordPopover.open}.
 */

/** A word in the text that opens the popover. */
const wordSelector = "a[data-cb-word]";

/** How long a mouse rests on a word, or away from both, before anything moves. */
const hoverDelay = 300;

/** What this needs of `<wa-popover>`. */
export interface PopoverElement extends HTMLElement {
  anchor: Element | null;
  open: boolean;
}

/** How the popover's save control is bound once it is drawn. */
export type BindSave = (within: HTMLElement, word: string) => void;

export class YueduWordPopover {
  private readonly popover: PopoverElement;
  private readonly rows: YueduWordRows;
  private readonly bindSave: BindSave;
  private readonly word: HTMLElement | null;
  private readonly link: HTMLAnchorElement | null;
  private readonly save: HTMLElement | null;
  private readonly body: HTMLElement | null;

  /** Whether hovering opened what is open, and leaving should close it. */
  private hovered = false;
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(properties: {
    readonly popover: PopoverElement;
    readonly rows: YueduWordRows;
    readonly bindSave: BindSave;
  }) {
    this.popover = properties.popover;
    this.rows = properties.rows;
    this.bindSave = properties.bindSave;
    this.word = this.popover.querySelector("[data-cb-yuedu-popover-word]");
    this.link = this.popover.querySelector("[data-cb-yuedu-popover-link]");
    this.save = this.popover.querySelector("[data-cb-yuedu-popover-save]");
    this.body = this.popover.querySelector("[data-cb-yuedu-popover-body]");

    this.popover.addEventListener("pointerenter", () => {
      this.settle();
    });
    this.popover.addEventListener("pointerleave", (event) => {
      if (event.pointerType === "mouse") {
        this.leave();
      }
    });
    this.popover.addEventListener("wa-after-hide", () => {
      this.popover.anchor?.setAttribute("aria-expanded", "false");
      this.hovered = false;
    });
  }

  /**
   * Take the presses and the hovering on every word in one part of the page.
   * A page with its questions under the text listens to each part.
   */
  listen(passage: HTMLElement): void {
    for (const word of passage.querySelectorAll<HTMLElement>(wordSelector)) {
      word.setAttribute("aria-haspopup", "dialog");
      word.setAttribute("aria-expanded", "false");
    }

    passage.addEventListener("click", (event) => {
      const word = this.wordOf(event.target);

      if (
        word === undefined ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      event.preventDefault();
      this.settle();

      if (this.popover.open && this.popover.anchor === word && !this.hovered) {
        this.popover.open = false;
      } else {
        this.open(word);
        this.hovered = false;
      }
    });

    passage.addEventListener("pointerover", (event) => {
      const word = this.wordOf(event.target);

      if (event.pointerType !== "mouse" || word === undefined) {
        return;
      }

      if (this.popover.open && this.popover.anchor === word) {
        this.settle();

        return;
      }

      this.later(() => {
        if (!this.popover.open || this.hovered) {
          this.open(word);
          this.hovered = true;
        }
      });
    });

    passage.addEventListener("pointerout", (event) => {
      const word = this.wordOf(event.target);

      if (
        event.pointerType === "mouse" &&
        word !== undefined &&
        !(
          event.relatedTarget instanceof Node &&
          word.contains(event.relatedTarget)
        )
      ) {
        this.leave();
      }
    });
  }

  /**
   * Show one word.
   * @param word - The word's link in the text.
   */
  open(word: HTMLElement): void {
    const said = word.dataset["cbWord"] ?? "";
    const parts = this.rows.parts(said);

    if (parts === undefined) {
      return;
    }

    this.word?.replaceChildren(parts.entry, parts.gloss);
    this.save?.replaceChildren(parts.save);
    this.link?.setAttribute("href", word.getAttribute("href") ?? "");
    this.body?.setAttribute("aria-label", `Reading and meaning of ${said}`);

    if (this.save !== null) {
      this.bindSave(this.save, said);
    }

    this.popover.anchor?.setAttribute("aria-expanded", "false");
    this.popover.anchor = word;
    word.setAttribute("aria-expanded", "true");
    this.popover.open = true;
  }

  /** The word a pointer or a press is on, if it is on one. */
  private wordOf(target: EventTarget | null): HTMLElement | undefined {
    return target instanceof Element
      ? (target.closest<HTMLElement>(wordSelector) ?? undefined)
      : undefined;
  }

  /** Close what hovering opened, unless the pointer comes back in time. */
  private leave(): void {
    this.later(() => {
      if (this.hovered) {
        this.popover.open = false;
      }
    });
  }

  private later(then: () => void): void {
    this.settle();
    this.timer = setTimeout(then, hoverDelay);
  }

  private settle(): void {
    clearTimeout(this.timer);
    this.timer = undefined;
  }
}
